import { Link } from 'react-router-dom';
import { Bell, User, LogOut, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';

export function Header() {
  const { user, logout, openAuthModal } = useAppStore();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-silver-100 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <img
            src="/images/tools-beaver-mascot-logo.png"
            alt="Tools Beaver"
            className="w-8 h-8 rounded-xl object-cover shadow-tim group-hover:opacity-90 transition-opacity"
          />
          <span className="font-display font-extrabold text-lg text-silver-800 leading-none">
            Tools<span className="text-garden-600">Beaver</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-silver-600">
          <Link to="/browse" className="hover:text-garden-600 transition-colors">Browse</Link>
          <Link to="/list-tool" className="hover:text-garden-600 transition-colors">List a Tool</Link>
          {user && (
            <>
              <Link to="/my-rentals" className="hover:text-garden-600 transition-colors">My Rentals</Link>
              <Link to="/my-listings" className="hover:text-garden-600 transition-colors">My Listings</Link>
            </>
          )}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {user ? (
            <>
              <button className="relative p-2 text-silver-500 hover:text-garden-600 transition-colors"
                aria-label="Notifications">
                <Bell size={20} />
              </button>
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-2xl hover:bg-silver-50 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-garden-100 flex items-center justify-center overflow-hidden">
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt={user.name ?? 'User'} className="w-full h-full object-cover" />
                    ) : (
                      <User size={15} className="text-garden-600" />
                    )}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-silver-700 max-w-24 truncate">
                    {user.name ?? 'Account'}
                  </span>
                </button>

                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-card-hover border border-silver-100 py-1 z-50">
                    <Link to="/profile" onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-silver-700 hover:bg-silver-50">
                      <User size={15} /> Profile
                    </Link>
                    <button onClick={() => { logout(); setMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50">
                      <LogOut size={15} /> Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => openAuthModal('login')}
                className="text-sm font-medium text-silver-600 hover:text-silver-900 px-3 py-2 transition-colors">
                Sign in
              </button>
              <button onClick={() => openAuthModal('register')}
                className="btn-primary text-sm py-2 px-4">
                Join free
              </button>
            </div>
          )}

          {/* Mobile menu toggle */}
          <button onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 text-silver-500 hover:text-silver-800 transition-colors"
            aria-label="Toggle menu">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile nav drawer */}
      {menuOpen && (
        <div className="md:hidden border-t border-silver-100 bg-white px-4 pb-4 space-y-1">
          {[
            { to: '/browse', label: 'Browse Tools' },
            { to: '/list-tool', label: 'List a Tool' },
            ...(user ? [
              { to: '/my-rentals', label: 'My Rentals' },
              { to: '/my-listings', label: 'My Listings' },
            ] : []),
          ].map(({ to, label }) => (
            <Link key={to} to={to} onClick={() => setMenuOpen(false)}
              className="block px-3 py-2.5 text-sm font-medium text-silver-700 hover:text-garden-600
                         hover:bg-garden-50 rounded-xl transition-colors">
              {label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
